import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent } from '@angular/material/legacy-autocomplete';
import {
  MatLegacySelect as MatSelect,
  MatLegacySelectChange as MatSelectChange,
} from '@angular/material/legacy-select';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { debounceTime, filter, takeUntil } from 'rxjs/operators';

import { RfqLotFormComponent } from 'app/modules/purchase-orders/rfq-lot-form/rfq-lot-form.component';
import { FeatureToggleService } from 'app/shared/services/feature-toggle.service';
import { ILocationOld } from 'v2/modules/location/interfaces/location.interface';
import { ILot } from 'v2/modules/lot/interfaces/lot.interface';
import { IRfqLot } from 'v2/modules/rfq/interfaces/rfq-lot.interface';
import { DecimalConfig } from 'v2/modules/shared/configs/decimal.config';
import { SnackBarConfig } from 'v2/modules/shared/configs/snackbar.config';
import { IWarehouseGroup } from 'v2/modules/shared/interfaces/location-warehouse.interface';
import { AssemblyOrderApiService } from 'v2/modules/shared/services/api/assembly-order-api.service';
import { LocationApiService } from 'v2/modules/shared/services/api/location/location-api.service';
import { LocationWarehouseGroupApiService } from 'v2/modules/shared/services/api/location/location-warehouse-group-api.service';
import { LotApiService } from 'v2/modules/shared/services/api/lot-api.service';
import { RfqApiService } from 'v2/modules/shared/services/api/rfq/rfq-api.service';
import { SystemSettingApiService } from 'v2/modules/shared/services/api/system-setting-api.service';
import { UnitOfMeasureApiService } from 'v2/modules/shared/services/api/unit-of-measure-api.service';
import { trackById, trackByValue } from 'v2/modules/shared/util';
import { IUnitConversionRule, IUnitOfMeasure } from 'v2/modules/unit-of-measure';
import { IExtraCost } from 'v2/standalone/extra-costs/extra-cost.types';
import { ICustomField, ECustomFieldObject, ICustomFieldValue } from 'v2/modules/custom-field/interfaces';

import { AssemblyOrderConfig } from '../../config';
import { EAssemblyOrderType } from '../../interfaces';

@Component({
  selector: 'lum-assembly-order-form',
  templateUrl: './assembly-order-form.component.html',
  styleUrls: ['./assembly-order-form.component.scss'],
})
export class AssemblyOrderFormComponent implements OnInit, OnDestroy {
  form :UntypedFormGroup;
  formRows :UntypedFormArray;
  bomItems :any[] = [];
  locationList :any[] = [];
  lotListByLocation :{ [key :number] :Partial<ILot>[] } = {};
  lineLocations :any[] = [];
  lineLots :any[] = [];
  loading :boolean = false;
  firstLoad :boolean = false;
  recomposingBomItems :boolean = false;
  recomposedItems :boolean = false;
  defaultWarehouseGroupId :number = 0;
  assemblyOrderConfig = AssemblyOrderConfig;
  rfqParams :{ [param :string] :string | string[] } = {
    rfqsWithBom: 'true',
    relations: 'boms,unitOfMeasures',
  };
  currentAssemblyOrderId :number = 0;
  currentAssemblyOrder :any = null;
  boms :any[] = [];
  uoms :any[] = [];
  warehouseGroupParams :Record<string, any> = {};
  isUpdate :boolean = false;
  isUpdateFirstLoad :boolean = false;
  loadingWarehouseGroups :boolean = false;
  loadingLocations :boolean = false;
  loadingLots :boolean = false;
  extraCosts :IExtraCost[] = [];
  alternateIdsFormArray: UntypedFormArray;
  customFields :ICustomField[] = [];
  eCustomFieldObject = ECustomFieldObject;
  loadingCustomFields :boolean = false;
  customFieldFormValuesJson: string = '[]';

  trackByValue = trackByValue;
  trackById = trackById;

  private onDestroy$ = new Subject();
  plannedQuantitySubject = new Subject<Event>();
  actualQuantitySubject = new Subject<Event>();
  modalPromise :Promise<any> | null = null;

  @ViewChild('lotMatSelect') lotMatSelect :MatSelect;

  constructor(
    private _feature :FeatureToggleService,
    private cd :ChangeDetectorRef,
    private fb :UntypedFormBuilder,
    private locationApiService :LocationApiService,
    private lotApiService :LotApiService,
    private snackBar :MatSnackBar,
    private assemblyOrderApiService :AssemblyOrderApiService,
    private unitOfMeasureApiService :UnitOfMeasureApiService,
    private warehouseGroupService :LocationWarehouseGroupApiService,
    private systemSettingsService :SystemSettingApiService,
    private router :Router,
    private route :ActivatedRoute,
    private rfqApiService :RfqApiService,
    private modalSvc :NgbModal,
  ) {}

  ngOnInit() :void {
    this.rfqApiService.resetData();
    this.initForm();
    this.getRouterParams();
    this.setupLotIdControl();
    this.alternateIdsFormArray = this.form.get('alternateIds') as UntypedFormArray;

    // Subscribe to custom field value changes
    const customFieldValuesArray = this.form.get('customFieldValues') as UntypedFormArray;
    customFieldValuesArray.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((customFieldValuesArrayValue: ICustomFieldValue[]) => {
        this.updateCustomFieldFormValuesToJson(customFieldValuesArrayValue);
      });
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
    if (this.modalPromise) {
      // Here we can't unsubscribe from the promise since it's not a subscription.
      // But, we can nullify the reference or handle any cleanup here if necessary.
      this.modalPromise = null;
    }
  }

  findLotById(lotId :number) :ILot | null {
    for (const lotGroup of this.lineLots) {
      if (lotGroup) {
        for (const key in lotGroup) {
          const foundLot = lotGroup[key].find((l) => l.id === lotId);
          if (foundLot) {
            return foundLot;
          }
        }
      }
    }

    return null;
  }

  onSave() :void {
    const formValue = this.form.value;
    const items = formValue.rows;

    // Validate all entries first
    let isValid = true;
    const orderLines = items.map((item :any) => {
      if (item.lotTracking) {
        const lotExists = this.findLotById(item.lotId);
        if (!lotExists) {
          isValid = false;
          this.snackBar.open(
            `Please enter a valid lot number for ${item.productName}`,
            'Close',
            { duration: 3000 },
          );
        }
      }

      return {
        rfqId: item.rfqId,
        actualQty: item.actualQty,
        actualUomId: item.actualUomId,
        plannedQty: Number(item.plannedQty),
        plannedUomId: item.plannedUomId,
        locationId: item.locationId,
        lotId: item.lotId > 0 ? item.lotId : null,
        warehouseId: item.warehouseId,
        bomBaseQuantity: Number(item.bomBaseQuantity),
      };
    });

    if (!isValid) {
      return;
    }

    const payload = this._feature.isActive('utah_FR-553_assembly_order_extra_costs')
      ? {
          rfqId: formValue.rfq.id,
          bomId: formValue.bomId,
          warehouseGroupId: formValue.warehouseGroupId,
          warehouseId: formValue.warehouseId,
          locationId: formValue.locationId,
          status: formValue.status,
          plannedQuantity: Number(formValue.plannedQuantity),
          plannedBaseQuantity: Number(formValue.plannedBaseQuantity),
          plannedUomId: formValue.uomId,
          actualQuantity: Number(formValue.actualQuantity),
          actualBaseQuantity: Number(formValue.actualBaseQuantity),
          actualUomId: formValue.actualUomId,
          lotId: formValue.lotId > 0 ? formValue.lotId : null,
          orderType: EAssemblyOrderType.ASSEMBLY,
          orderLines: orderLines,
          extraCosts: formValue?.extraCosts ?? [],
          alternateIds: formValue?.alternateIds ?? [],
          customFieldValues: this.formattedCustomFieldValues,
        }
      : {
          rfqId: formValue.rfq.id,
          bomId: formValue.bomId,
          warehouseGroupId: formValue.warehouseGroupId,
          warehouseId: formValue.warehouseId,
          locationId: formValue.locationId,
          status: formValue.status,
          plannedQuantity: Number(formValue.plannedQuantity),
          plannedBaseQuantity: Number(formValue.plannedBaseQuantity),
          plannedUomId: formValue.uomId,
          actualQuantity: Number(formValue.actualQuantity),
          actualBaseQuantity: Number(formValue.actualBaseQuantity),
          actualUomId: formValue.actualUomId,
          lotId: formValue.lotId > 0 ? formValue.lotId : null,
          orderType: EAssemblyOrderType.ASSEMBLY,
          orderLines: orderLines,
          alternateIds: formValue?.alternateIds ?? [],
          customFieldValues: this.formattedCustomFieldValues,
        };

    this.loading = true;
    if (this.currentAssemblyOrder) {
      this.assemblyOrderApiService
        .update(this.currentAssemblyOrder?.id, payload)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
          next: (response :any) => {
            this.loading = false;
            this.snackBar.open(`Successfully updated assembly order.`, 'Success', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
            this.cd.detectChanges();
            this.router.navigate(['/assembly-orders']);
          },
          error: (response :any) => {
            this.loading = false;
            this.snackBar.open(`Failed to update assembly order.`, 'Close', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
            this.cd.detectChanges();
          },
        });
    } else {
      this.assemblyOrderApiService
        .create(payload)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
          next: (response :any) => {
            this.loading = false;
            this.snackBar.open(`Successfully created assembly order.`, 'Success', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
            this.cd.detectChanges();
            this.router.navigate(['/assembly-orders']);
          },
          error: (response :any) => {
            this.loading = false;
            this.snackBar.open(`Failed to create assembly order.`, 'Close', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
            this.cd.detectChanges();
          },
        });
    }
  }

  onLineSelectWarehouseEvent(data :{
    id :number;
    index :number;
    value :number;
  }) :void {
    const { id, index, value } = data;
    this.loadLineRfqLocations(id, value, index, !!this.currentAssemblyOrder, true);
  }

  onLineSelectLocationEvent(data :{
    id :number;
    index :number;
    value :number;
  }) :void {
    const { id, index, value } = data;
    const row = this.formRows.controls[index];
    const warehouseId = row?.value?.warehouseId ?? 0;
    const location = this.lineLocations?.[id]?.[index]?.[warehouseId]?.find(
      (location :any) => location.id === value,
    );

    if (row) {
      const availableQty = location?.rfq_stock_quantity ?? 0;
      row.get('availableQty')?.patchValue(availableQty);
      const plannedBaseControl = row.get('plannedBaseQty');
      if (plannedBaseControl) {
        plannedBaseControl.setValidators([Validators.required]);
        plannedBaseControl.updateValueAndValidity();
        plannedBaseControl.markAsTouched();
      }
    }

    this.loadLotsByRfqId(id, value, index);
  }

  onLineCreateNewLotEvent(data :{
    index :number;
    createdLotId :number;
    lineLots :any[];
  }) :void {
    const { index, createdLotId, lineLots } = data;
    this.formRows.controls[index].get('lotId')?.patchValue(createdLotId);
    this.lineLots = lineLots;
  }

  onSearchLotNumberEvent(data :{
    index :number;
    locationId :number;
    rfqId :number;
    searchValue :string;
  }) :void {
    const { index, locationId, rfqId, searchValue } = data;

    this.loadLotsByRfqId(rfqId, locationId, index, true, searchValue);
  }

  onLineUpdateQuantityEvent(data :{ index :number; value :number }) :void {
    this.recomposedItems = true;
  }

  onSelectUnitOfMeasureEvent(event :MatSelectChange) :void {
    this.getConversionRule(event.value);
  }

  onSelectWarehouseGroupEvent(warhouseGroupEvent :IWarehouseGroup) :void {
    const warehouseGroupValue = this.warehouseGroupsControl?.value?.find(
      (warehouseGroup :IWarehouseGroup) =>
        warehouseGroup.id === warhouseGroupEvent?.id,
    );

    if (warehouseGroupValue) {
      this.warehouseGroupIdControl?.patchValue(warehouseGroupValue?.id);
      this.warehouseIdControl?.patchValue(
        warehouseGroupValue?.warehouses[0]?.id ?? 0,
      );
      this.formRows?.controls?.forEach((row :any, index :number) => {
        row
          .get('warehouseId')
          ?.patchValue(warehouseGroupValue?.warehouses[0]?.id ?? 0);
        this.loadLineRfqLocations(
          row?.value?.rfqId ?? 0,
          warehouseGroupValue?.warehouses[0]?.id ?? 0,
          index,
        );
      });
      this.recomposedItems = true;
      this.loadComposedBomItems(true);
      this.loadLocationsBySelectedWarehouse();
    }
  }

  onSelectWarehouseEvent(event :MatSelectChange) :void {
    this.recomposedItems = true;
    this.loadComposedBomItems(true);
    this.loadLocationsBySelectedWarehouse();
  }

  onSelectLocationEvent(event :MatSelectChange) :void {
    this.loadLotsBySelectedLocation();
  }

  onQuantityChange(event :Event) :void {
    const plannedQuantity = (event.target as HTMLInputElement).value;
    const conversionFactor = this.form.get('conversionFactor')?.value;

    this.plannedBaseQuantityControl?.patchValue(
      Number(plannedQuantity) * conversionFactor,
    );
    if (!this.currentAssemblyOrder) {
      this.actualQuantityControl?.patchValue(plannedQuantity);

      const actualQuantity = this.form.get('actualQuantity')?.value;
      this.actualBaseQuantityControl?.patchValue(
        Number(actualQuantity) * conversionFactor,
      );
    }
    this.recomposedItems = true;
    this.loadComposedBomItems(false, true);
  }

  onActualQuantityChange(event :Event) :void {
    const actualQuantity = (event.target as HTMLInputElement).value;
    const conversionFactor = this.form.get('conversionFactor')?.value;

    this.actualBaseQuantityControl?.patchValue(
      Number(actualQuantity) * conversionFactor,
    );
  }

  onSelectRfqEvent({ rfq } :any) :void {
    this.firstLoad = true;
    this.warehouseGroupParams = {};
    this.boms = rfq.boms ?? [];
    this.uoms = rfq.unit_of_measures ?? [];
    rfq = this.convertUnderscoreToCamelCase(rfq);

    const autoSelectBom = this.boms[0].id ?? null;

    this.rfqControl?.patchValue(rfq);
    this.uomIdControl?.patchValue(rfq.defaultPurchaseUnitId);
    this.actualUomIdControl?.patchValue(rfq.defaultPurchaseUnitId);
    this.bomIdControl?.patchValue(autoSelectBom);
    this.getConversionRule(rfq.defaultPurchaseUnitId, true);
    this.getDefaultWarehouseGroupId();

    this.cd.detectChanges();
  }

  onInputPlannedQuantityEvent(event :Event) :void {
    this.plannedQuantitySubject.next(event);
  }

  onInputActualQuantityEvent(event :Event) :void {
    this.actualQuantitySubject.next(event);
  }

  private convertUnderscoreToCamelCase(
    obj :Record<string, any>,
  ) :Record<string, any> {
    if (typeof obj !== 'object' || obj === null) {
      return obj;
    }

    const camelCaseObj :Record<string, any> = {};

    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const camelCaseKey = key.replace(/_(\w)/g, (match, p1) => p1.toUpperCase());
        camelCaseObj[camelCaseKey] = this.convertUnderscoreToCamelCase(obj[key]);
      }
    }

    return camelCaseObj;
  }

  onSelectBomEvent(event ?:MatSelectChange) :void {
    if (this.bomIdControl?.value) {
      this.recomposedItems = true;
      this.loadComposedBomItems();
    }
  }

  onCreateNewLot() :void {
    // Then close the mat-select programmatically
    if (this.lotMatSelect) {
      this.lotMatSelect.close();
    }

    //This code is subject for change until the implementation from DV will be published
    const modalRef = this.modalSvc.open(RfqLotFormComponent, {
      size: 'lg',
    });
    modalRef.componentInstance.rfqId = this.rfqControl?.value?.id;
    modalRef.componentInstance.quantityOriginal =
      this.plannedQuantityControl?.value || 0;
    this.modalPromise = modalRef.result.then(
      (selectedRfqLot) => {
        if (selectedRfqLot) {
          this.pushCreatedLot(selectedRfqLot);
        }
      },
      (error) => {
        this.snackBar.open(`Failed to create Rfq Lot.`, 'Close', {
          duration: SnackBarConfig.DEFAULT.duration,
        });
      },
    );
  }

  displayFnLot(option :any) :string {
    if (!option) return '';

    // Handling both object and id as input
    const lot =
      typeof option === 'object'
        ? option
        : this.lots.find((lot) => lot.id === option);

    return lot ? lot.lotNumber : '';
  }

  onSelectOption(event :MatAutocompleteSelectedEvent) :void {
    this.lotIdControl.patchValue(event.option.value);
  }

  onSearchWarehouseGroups(label :string) :void {
    this.warehouseGroupParams = {};
    this.loadWarehouseGroups({ label });
  }

  displayFnWarehouseGroup(warehouseGroup :any) :string {
    return warehouseGroup?.label ?? '';
  }

  onLazyLoadWarehouseGroup() :void {
    let page = this.warehouseGroupParams?.page ?? 1;
    const lastPage = this.warehouseGroupParams?.last_page ?? 0;
    if (lastPage > page) {
      this.warehouseGroupParams.page = ++page;
      this.loadWarehouseGroups({ page: this.warehouseGroupParams.page }, false);
    }
  }

  onResetQuantities() :void {
    this.plannedQuantityControl?.patchValue(
      this.currentAssemblyOrder?.plannedQuantity,
    );
    this.plannedBaseQuantityControl?.patchValue(
      this.currentAssemblyOrder?.plannedBaseQuantity,
    );
    this.initFormRows(true);
  }

  onResetActualQuantities() :void {
    this.actualQuantityControl?.patchValue(
      this.currentAssemblyOrder?.actualQuantity,
    );
    this.actualBaseQuantityControl?.patchValue(
      this.currentAssemblyOrder?.actualBaseQuantity,
    );
  }

  onCustomFieldChange(value: string): void {
    const customFieldValues = JSON.parse(value);
    const customFieldValuesArray = this.form.get('customFieldValues') as UntypedFormArray;
    
    // Clear and rebuild the array in one operation
    customFieldValuesArray.clear();
    
    // Create and add form groups using snake_case properties
    customFieldValues.forEach((customFieldValue: any) => {
      const formGroup = this.fb.group({
        custom_field_id: [customFieldValue.custom_field],
        value_string: [customFieldValue.value],
        value_number: [customFieldValue.value],
        value_date: [customFieldValue.value],
        custom_field: [customFieldValue.custom_field]
      });
      customFieldValuesArray.push(formGroup);
    });
    // Ensure the global variable is updated
    this.updateCustomFieldFormValuesToJson(customFieldValuesArray.value);
  }

  private pushCreatedLot(selectedRfqLot :IRfqLot) :void {
    this.lotIdControl?.patchValue(selectedRfqLot?.id);
    this.lotListByLocation[this.locationIdControl?.value].push({
      id: selectedRfqLot?.id,
      lotNumber: selectedRfqLot?.lot_number,
      manufacturerCode: selectedRfqLot?.manufacturer_code,
      originalQuantity: selectedRfqLot?.quantity_original,
      productionDate: selectedRfqLot?.production_date,
      rfqId: selectedRfqLot?.rfq_id,
      updatedAt: selectedRfqLot?.updated_at,
      createdAt: selectedRfqLot?.created_at,
      expiryDate: selectedRfqLot?.expiry_date,
    });
  }

  get rfqControl() :AbstractControl | null {
    return this.form.get('rfq');
  }

  get plannedQuantityControl() :AbstractControl | null {
    return this.form.get('plannedQuantity');
  }

  get actualQuantityControl() :AbstractControl | null {
    return this.form.get('actualQuantity');
  }

  get bomIdControl() :AbstractControl | null {
    return this.form.get('bomId');
  }

  get uomIdControl() :AbstractControl | null {
    return this.form.get('uomId');
  }

  get actualUomIdControl() :AbstractControl | null {
    return this.form.get('actualUomId');
  }

  get warehouseGroupIdControl() :AbstractControl | null {
    return this.form.get('warehouseGroupId');
  }

  get warehouseIdControl() :AbstractControl | null {
    return this.form.get('warehouseId');
  }

  get locationIdControl() :AbstractControl | null {
    return this.form.get('locationId');
  }

  get lotIdControl() :AbstractControl | null {
    return this.form.get('lotId');
  }

  get plannedBaseQuantityControl() :AbstractControl | null {
    return this.form.get('plannedBaseQuantity');
  }

  get actualBaseQuantityControl() :AbstractControl | null {
    return this.form.get('actualBaseQuantity');
  }

  get conversionFactorControl() :AbstractControl | null {
    return this.form.get('conversionFactor');
  }

  get warehouseGroupsControl() :AbstractControl | null {
    return this.form.get('warehouseGroups');
  }

  get statusControl() :AbstractControl | null {
    return this.form.get('status');
  }

  get rfqPlannedUom() :IUnitOfMeasure | null {
    const uomId = this.uomIdControl?.value;
    const uoms = this.uoms;
    if (!uoms) {
      return null;
    }

    return uoms.find((uom :IUnitOfMeasure) => uom.id === uomId) ?? null;
  }

  get rfqActualUom() :IUnitOfMeasure | null {
    const uomId = this.rfqControl?.value?.defaultPurchaseUnitId;
    const uoms = this.uoms;
    if (!uoms) {
      return null;
    }

    return uoms.find((uom :IUnitOfMeasure) => uom.id === uomId) ?? null;
  }

  get warehouses() :any[] {
    if (
      !this.warehouseGroupsControl ||
      this.warehouseGroupsControl?.value?.length <= 0 ||
      !this.warehouseGroupIdControl
    ) {
      return [];
    }

    const warehouseGroup = this.warehouseGroupsControl?.value?.find(
      (warehouseGroup :any) =>
        warehouseGroup.id === this.warehouseGroupIdControl?.value,
    );

    return warehouseGroup?.warehouses ?? [];
  }

  get locations() :any[] {
    if (!this.warehouseIdControl || this.warehouseIdControl?.value?.length <= 0) {
      return [];
    }

    return this.locationList[this.warehouseIdControl?.value] ?? [];
  }

  get lots() :any[] {
    if (!this.locationIdControl || this.locationIdControl?.value?.length <= 0) {
      return [];
    }

    return this.lotListByLocation[this.locationIdControl?.value] ?? [];
  }

  get hasLotTracking() :boolean {
    return this.formRows?.value?.some(
      (item :any) => item.lotTracking === true || item.lotTracking === 1,
    );
  }

  get selectedWarehouseGroup() :any | null {
    const warehouseGroupId = this.warehouseGroupIdControl?.value;
    if (this.warehouseGroupsControl?.value?.length <= 0) {
      return null;
    }

    return (
      this.warehouseGroupsControl?.value?.find(
        (warehouseGroup :any) => warehouseGroup.id === warehouseGroupId,
      ) ?? null
    );
  }

  get selectedWarehouse() :any | null {
    const warehouseId = this.warehouseIdControl?.value;
    if (this.warehouses?.length <= 0) {
      return null;
    }

    return (
      this.warehouses?.find((warehouse :any) => warehouse.id === warehouseId) ??
      null
    );
  }

  get selectedLocation() :any | null {
    const locationId = this.locationIdControl?.value;
    if (this.locations?.length <= 0) {
      return null;
    }

    return (
      this.locations?.find((location :any) => location.id === locationId) ?? null
    );
  }

  private getRouterParams() :void {
    this.route.params
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((params :Params) => {
        const id = params['id'];
        if (id) {
          this.isUpdate = true;
          this.currentAssemblyOrderId = id;
          this.loadAssemblyOrder();
        }
        this.initPlannedQuantitySubject();
        this.initActualQuantitySubject();
      });
  }

  private loadAssemblyOrder() :void {
    this.loadingCustomFields = true;
    this.assemblyOrderApiService
      .getById(this.currentAssemblyOrderId, {
        relations: [
          'bom',
          'extraCosts',
          'lot',
          'orderLines.location',
          'orderLines.lot',
          'orderLines.rfq',
          'rfq.boms',
          'rfq.unitOfMeasures',
          'alternateIds',
          'customFieldValues.customField',
        ].join(','),
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response :any) => {
          if (response) {
            this.firstLoad = true;
            this.isUpdateFirstLoad = true;
            this.currentAssemblyOrder = response;
            this.boms = response?.rfq?.boms ?? [];
            this.uoms = response?.rfq?.unitOfMeasures ?? [];

            // Handle custom field values first
            if (Array.isArray(response?.customFieldValues)) {
              const customFieldValuesArray = this.form.get('customFieldValues') as UntypedFormArray;
              customFieldValuesArray.clear();
              response.customFieldValues.forEach((customFieldValue: any) => {
                customFieldValuesArray.push(
                  this.fb.group({
                    custom_field_id: [customFieldValue.custom_field_id],
                    value_string: [customFieldValue.value_string],
                    value_number: [customFieldValue.value_number],
                    value_date: [customFieldValue.value_date],
                    custom_field: [customFieldValue.custom_field]
                  })
                );
              });
              // Update custom field form values
              this.updateCustomFieldFormValuesToJson(customFieldValuesArray.value);
            }

            // Then handle the rest of the form values
            this.rfqControl?.patchValue(response?.rfq);
            this.bomIdControl?.patchValue(response?.bomId);
            this.uomIdControl?.patchValue(response?.plannedUomId);
            this.actualUomIdControl?.patchValue(response?.actualUomId);
            this.warehouseGroupIdControl?.patchValue(response?.warehouseGroupId);
            this.warehouseIdControl?.patchValue(response?.warehouseId);
            this.locationIdControl?.patchValue(response?.locationId);
            this.lotIdControl?.patchValue(response?.lotId);
            this.plannedQuantityControl?.patchValue(response?.plannedQuantity);
            this.actualQuantityControl?.patchValue(response?.actualQuantity);
            this.plannedBaseQuantityControl?.patchValue(
              response?.plannedBaseQuantity,
            );
            this.actualBaseQuantityControl?.patchValue(
              response?.actualBaseQuantity,
            );
            this.statusControl?.patchValue(response?.status);

            if (response.locationId) {
              // #sanitycheck
              if (!this.lotListByLocation[response.locationId]) {
                this.lotListByLocation[response.locationId] = [];
              }
              if (response.lot) {
                // don't add falsey to lot list [UTAH-213]
                this.lotListByLocation[response.locationId].push(response.lot);
              }
            }

            for (const orderLine of response.orderLines) {
              if (!this.lineLots[orderLine.rfqId]) {
                this.lineLots[orderLine.rfqId] = {};
              }
              if (!this.lineLots[orderLine.rfqId][orderLine.locationId]) {
                this.lineLots[orderLine.rfqId][orderLine.locationId] = [];
              }
              this.lineLots[orderLine.rfqId][orderLine.locationId].push(
                orderLine.lot,
              );
            }

            if (Array.isArray(response?.extraCosts)) {
              this.extraCosts = response?.extraCosts;
            }

            // Handle alternate IDs
            if (Array.isArray(response?.alternateIds)) {
              this.alternateIdsFormArray.clear();
              response.alternateIds.forEach((alternateIdObj: any) => {
                // Use the correct property name for the string value
                this.alternateIdsFormArray.push(this.fb.control(alternateIdObj.alternateId));
              });
            }

            this.getConversionRule(response?.plannedUomId);
            this.loadWarehouseGroups();
          }
          this.loadingCustomFields = false;
          this.cd.detectChanges();
        },
        error: (response :any) => {
          this.loadingCustomFields = false;
          this.snackBar.open(response.error, 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  private loadComposedBomItems(
    fromSelectWarehouseGroupTriggerEvent :boolean = false,
    fromPlannedQuantityChange :boolean = false,
  ) :void {
    if (!this.firstLoad && this.bomIdControl?.value <= 0) {
      return;
    }

    const plannedBaseQty = this.plannedBaseQuantityControl?.value;
    if (plannedBaseQty < 0) {
      return;
    }

    this.recomposingBomItems = true;
    this.assemblyOrderApiService
      .getComposedBomItems(this.bomIdControl?.value, {
        plannedBaseQty: plannedBaseQty,
        warehouseGroupId: this.warehouseGroupIdControl?.value,
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response :any) => {
          this.recomposedItems = true;
          this.recomposingBomItems = false;
          if (response.bomItems?.length) {
            this.bomItems = response.bomItems;
            this.initFormRows(
              fromSelectWarehouseGroupTriggerEvent,
              fromPlannedQuantityChange,
            );
          }

          this.cd.detectChanges();
        },
        error: (response :any) => {
          this.recomposingBomItems = false;
          this.snackBar.open(response.error, 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  private getDefaultWarehouseGroupId() :void {
    if (this.currentAssemblyOrder) {
      this.loadWarehouseGroups();

      return;
    }

    this.systemSettingsService
      .getSettingsByKey('default_warehouse_group_id')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (warehouseGroupId :string) => {
          this.defaultWarehouseGroupId = Number(warehouseGroupId);
          this.warehouseGroupIdControl?.patchValue(this.defaultWarehouseGroupId);
          this.loadWarehouseGroups();
          this.cd.detectChanges();
        },
        error: () => {
          this.snackBar.open('Failed to get Warehouse Group ID.', 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  private loadWarehouseGroups(
    params :{ [key :string] :string | string[] } = {},
    isNotLazyLoadWarehouseGroup = true,
  ) :void {
    this.loadingWarehouseGroups = true;
    this.warehouseGroupService
      .getWarehouseGroups({
        relations: 'warehouses',
        ...params,
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response :any) => {
          if (response?.data?.length > 0) {
            this.loading = false;

            //When onLazyLoadWarehouseGroup is not yet triggered, proceed to regular implementation
            if (isNotLazyLoadWarehouseGroup) {
              this.warehouseGroupsControl?.patchValue(response.data);
            } else {
              // Here we append the new data instead of replacing the old data
              const existingData = this.warehouseGroupsControl?.value || [];
              this.warehouseGroupsControl?.patchValue([
                ...existingData,
                ...response.data,
              ]);
            }
            this.updateDefaultWarehouseId();
            this.loadingWarehouseGroups = false;
          }

          //get last page for lazy load
          if (response?.meta) {
            this.warehouseGroupParams.last_page = response?.meta?.last_page;
          }
          this.cd.detectChanges();
        },
        error: (response :any) => {
          this.loading = false;
          this.snackBar.open(response.error, 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
          this.loadingWarehouseGroups = false;
        },
      });
  }

  private loadLineRfqLocations(
    rfqId :number,
    warehouseId :number,
    index :number,
    forUpdate :boolean = false,
    triggerSelectWarehouseTriggerEvent :boolean = false,
  ) :void {
    const existingIndex =
      this.lineLocations?.[rfqId]?.[index]?.[warehouseId]?.length > 0 || null;

    if (existingIndex && !forUpdate) {
      this.setWarehouseLocation(
        rfqId,
        warehouseId,
        index,
        triggerSelectWarehouseTriggerEvent,
      );
      this.loadingLocations = false;
      this.cd.detectChanges();

      return;
    }

    this.assemblyOrderApiService
      .getItemLocationsByRfq(rfqId, {
        warehouseId: warehouseId.toString(),
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response :any) => {
          // Determine the value to assign based on the condition
          const locationData = response?.length > 0 ? response : this.locations;
          if (response?.length === 0 && triggerSelectWarehouseTriggerEvent) {
            this.getLineItemLocationsByParentId(rfqId, warehouseId, index);
          } else {
            // Update the lineLocations in a single operation
            this.updateLineLocations(rfqId, index, warehouseId, locationData);
          }

          this.setWarehouseLocation(
            rfqId,
            warehouseId,
            index,
            triggerSelectWarehouseTriggerEvent,
          );

          this.loadingLocations = false;
          this.cd.detectChanges();
        },
        error: (response :any) => {
          this.snackBar.open(response.error, 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  private getLineItemLocationsByParentId(
    rfqId :number,
    warehouseId :number,
    index ?:number,
  ) :void {
    this.loadingLocations = true;
    this.locationApiService
      .getLocationsByParentId({
        all: '1',
        parent_id: warehouseId.toString(),
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response :ILocationOld[]) => {
          this.locationList[warehouseId] = response;
          // Update the lineLocations in a single operation
          this.updateLineLocations(rfqId, index, warehouseId, response);
          this.setWarehouseLocation(rfqId, warehouseId, index, true);
          this.loadingLocations = false;
          this.cd.detectChanges();
        },
        error: (response :any) => {
          this.snackBar.open(response.error, 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
          this.loadingLocations = false;
          this.cd.detectChanges();
        },
      });
  }

  private setWarehouseLocation(
    rfqId :number,
    warehouseId :number,
    index :number,
    fromSelectWarehouseGroupTriggerEvent :boolean = false,
  ) :void {
    let defaultWarehouseId = warehouseId || this.warehouseIdControl?.value;
    const defaultLocationId =
      this.lineLocations[rfqId]?.[index]?.[warehouseId]?.[0]?.id ||
      this.locationIdControl?.value ||
      0;

    // Patch the warehouseId with the determined default or control value
    if (
      this.shouldUseBomLineItemWarehouse(
        index,
        defaultWarehouseId,
        fromSelectWarehouseGroupTriggerEvent,
      )
    ) {
      defaultWarehouseId =
        this.currentAssemblyOrder?.orderLines[index]?.location?.parentId;
      this.patchWarehouseId(index, defaultWarehouseId);
      if (!this.lineLocations[rfqId]?.[index]?.[defaultWarehouseId]) {
        this.fetchAndSetItemLocations(rfqId, index, defaultWarehouseId);
      }
    } else {
      this.patchWarehouseId(index, defaultWarehouseId);
    }

    // This logic will identify on edit AO on setting the location
    if (this.shouldPatchLocationId(index, fromSelectWarehouseGroupTriggerEvent)) {
      this.patchLocationId(
        index,
        this.currentAssemblyOrder?.orderLines[index]?.locationId,
      );
    } else {
      if (
        this.shouldUseRecomposedLocationId(
          index,
          fromSelectWarehouseGroupTriggerEvent,
        )
      ) {
        this.patchLocationId(
          index,
          this.currentAssemblyOrder?.orderLines[index]?.locationId,
        );
      } else {
        // Simplified logic to determine the locationId value to patch
        this.patchLocationId(index, defaultLocationId);
      }
    }
  }

  private shouldUseRecomposedLocationId(
    index :number,
    fromSelectWarehouseGroupTriggerEvent :boolean,
  ) :boolean {
    return (
      this.currentAssemblyOrder?.orderLines[index]?.locationId &&
      !this.recomposedItems &&
      !fromSelectWarehouseGroupTriggerEvent
    );
  }

  private shouldPatchLocationId(
    index :number,
    fromSelectWarehouseGroupTriggerEvent :boolean,
  ) :boolean {
    return (
      this.currentAssemblyOrder?.orderLines[index]?.locationId &&
      this.warehouseIdControl?.value === this.currentAssemblyOrder?.warehouseId &&
      this.firstLoad &&
      !fromSelectWarehouseGroupTriggerEvent
    );
  }

  private shouldUseBomLineItemWarehouse(
    index :number,
    defaultWarehouseId :number,
    fromSelectWarehouseGroupTriggerEvent :boolean,
  ) :boolean {
    return (
      this.currentAssemblyOrder?.orderLines[index]?.location?.parentId !==
        defaultWarehouseId &&
      this.isWarehouseGroupIdMatchOnEdit &&
      this.isWarehouseIdMatchOnEdit &&
      this.firstLoad &&
      !fromSelectWarehouseGroupTriggerEvent
    );
  }

  private patchWarehouseId(index :number, warehouseId :number) :void {
    this.formRows.controls[index].get('warehouseId')?.patchValue(warehouseId);
  }

  private patchLocationId(index :number, locationId :number) :void {
    this.formRows.controls[index].get('locationId')?.patchValue(locationId);
  }

  private fetchAndSetItemLocations(
    rfqId :number,
    index :number,
    warehouseId :number,
  ) :void {
    this.assemblyOrderApiService
      .getItemLocationsByRfq(rfqId, { warehouseId: warehouseId.toString() })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response :any) => {
          this.updateLineLocations(rfqId, index, warehouseId, response);
        },
        error: (response :any) => {
          this.showSnackBarError(response.error);
        },
      });
  }

  private updateLineLocations(
    rfqId :number,
    index :number,
    warehouseId :number,
    locations :any,
  ) :void {
    this.lineLocations[rfqId] = {
      ...this.lineLocations[rfqId],
      [index]: {
        ...this.lineLocations[rfqId]?.[index],
        [warehouseId]: locations,
      },
    };
    this.cd.detectChanges();
  }

  private showSnackBarError(errorMessage :string) :void {
    this.snackBar.open(errorMessage, 'Fail', {
      duration: SnackBarConfig.DEFAULT.duration,
    });
  }

  private loadLocationsBySelectedWarehouse(forUpdate :boolean = false) :void {
    const warehouseId = this.warehouseIdControl?.value ?? 0;
    const emptyWarehouseId = !warehouseId || warehouseId <= 0;
    if (emptyWarehouseId) {
      return;
    }

    if (this.locationList[warehouseId] && !forUpdate) {
      this.locationIdControl?.patchValue(
        this.locationList[warehouseId]?.[0]?.id ?? 0,
      );

      return;
    }

    if ((!this.currentAssemblyOrder || this.recomposedItems) && !forUpdate) {
      this.locationIdControl?.patchValue(null);
    }

    this.loadingLocations = true;
    this.locationApiService
      .getLocationsByParentId({
        all: '1',
        parent_id: warehouseId,
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response :any) => {
          if (response?.length > 0) {
            if (
              (!this.currentAssemblyOrder || this.recomposedItems) &&
              !forUpdate
            ) {
              this.locationIdControl?.patchValue(response[0]?.id ?? 0);
            }
            this.locationList[warehouseId] = response;
            this.loadLotsBySelectedLocation(forUpdate);
          }

          if (this.boms[0].id) {
            this.onSelectBomEvent();
          }
          this.loadingLocations = false;
          this.cd.detectChanges();
        },
        error: (response :any) => {
          this.snackBar.open(response.error, 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
          this.loadingLocations = false;
          this.cd.detectChanges();
        },
      });
  }

  private loadLotsBySelectedLocation(
    forUpdate :boolean = false,
    searchValue :string = '',
    bomItemRfqId :number = 0,
  ) :void {
    const locationId = this.locationIdControl?.value ?? 0;
    const lotId = this.lotIdControl?.value ?? 0;
    const emptyLocationId = !locationId || locationId <= 0;
    if (emptyLocationId) {
      return;
    }

    if ((!this.currentAssemblyOrder || this.recomposedItems) && !forUpdate) {
      this.lotIdControl?.patchValue(null);
    }

    this.loadingLots = true;
    this.lotApiService
      .get({
        rfqId: bomItemRfqId === 0 ? this.rfqControl?.value?.id : bomItemRfqId,
        lotNumber: searchValue,
        sortDirection: 'desc',
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response :any) => {
          if ((!this.currentAssemblyOrder || this.recomposedItems) && !forUpdate) {
            const newLotId = lotId > 0 ? lotId : (response?.data[0]?.id ?? 0);
            this.lotIdControl?.patchValue(newLotId);
          }

          if (!this.lotListByLocation[locationId]) {
            this.lotListByLocation[locationId] = [];
          }

          if (response.data.length > 0 && forUpdate) {
            response.data.forEach((item) => {
              if (
                !this.lotListByLocation[locationId].some(
                  (existingItem) => existingItem.id === item.id,
                )
              ) {
                this.lotListByLocation[locationId].push(item);
              }
            });
          } else {
            this.lotListByLocation[locationId] = response.data;
          }

          this.loadingLots = false;
          this.cd.detectChanges();
        },
        error: (response :any) => {
          this.snackBar.open(response.error, 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
          this.loadingLots = false;
          this.cd.detectChanges();
        },
      });
  }

  private loadLotsByRfqId(
    rfqId :number,
    locationId :number,
    index :number,
    forUpdate :boolean = false,
    searchValue :string = '',
  ) :void {
    const emptyLocationId = !locationId || locationId <= 0;
    if (emptyLocationId) {
      return;
    }

    this.lotApiService
      .getByIdV2({
        id: rfqId,
        params: {
          locationId: locationId.toString(),
          searchQuery: searchValue,
          sortDirection: 'desc',
        },
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response :any) => {
          if ((!this.currentAssemblyOrder || this.recomposedItems) && !forUpdate) {
            this.formRows?.['controls'][index]
              .get('lotId')
              ?.patchValue(response.data?.[0]?.id ?? 0);
          }

          this.lineLots[rfqId] = {
            ...this.lineLots[rfqId],
            [locationId]: response.data,
          };

          this.cd.detectChanges();
        },
        error: (response :any) => {
          this.snackBar.open(response.error, 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  private updateDefaultWarehouseId() :void {
    if (this.currentAssemblyOrder) {
      this.loadLocationsBySelectedWarehouse(true);

      return;
    }

    const warehouseGroupValue = this.warehouseGroupsControl?.value?.find(
      (warehouseGroup :any) =>
        warehouseGroup.id === this.warehouseGroupIdControl?.value,
    );

    if (warehouseGroupValue) {
      this.warehouseIdControl?.patchValue(
        warehouseGroupValue?.warehouses[0]?.id ?? 0,
      );
      this.loadLocationsBySelectedWarehouse();
    }
  }

  private getConversionRule(uomId :number, fromRfqSelect :boolean = false) :void {
    const rfq = this.rfqControl?.value ?? null;
    this.unitOfMeasureApiService
      .getConversionRule({
        rfqId: rfq.id,
        baseUnitOfMeasureId: rfq.defaultPurchaseUnitId,
        convertedUnitOfMeasureId: uomId.toString(),
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response :IUnitConversionRule) => {
          const plannedQuantity = this.plannedQuantityControl?.value ?? 0;
          let conversionFactor = 1;
          if (response.conversionFactor) {
            conversionFactor = response.conversionFactor;
          }

          this.conversionFactorControl?.patchValue(conversionFactor);
          this.plannedBaseQuantityControl?.patchValue(
            plannedQuantity * conversionFactor,
          );
          if (!fromRfqSelect) {
            this.loadComposedBomItems();
          }
        },
        error: (response) => {
          this.snackBar.open(response.error, 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  private isFieldRequiredValidator(isRequired :boolean) :Validators {
    return (control :UntypedFormControl) => {
      return isRequired ? Validators.required(control) : null;
    };
  }

  private initForm() :void {
    this.form = this.fb.group({
      rows: this.fb.array([]),
      rfq: [null, [Validators.required]],
      bomId: [null, [Validators.required]],
      plannedQuantity: [
        1,
        [Validators.required, Validators.min(DecimalConfig.minValue)],
      ],
      actualQuantity: [
        1,
        [Validators.required, Validators.min(DecimalConfig.minValue)],
      ],
      uomId: [null, [Validators.required]],
      actualUomId: [null, [Validators.required]],
      warehouseGroupId: [null, [Validators.required]],
      warehouseId: [null, [Validators.required]],
      locationId: [null, [Validators.required]],
      lotId: [null],
      status: [
        this.assemblyOrderConfig.assemblyOrderStatus.DRAFT,
        [Validators.required],
      ],
      plannedBaseQuantity: [1],
      actualBaseQuantity: [1],
      conversionFactor: [1],
      warehouseGroups: [[]],
      alternateIds: this.fb.array([]),
      customFieldValues: this.fb.array([]),
    });
    this.displayFnLot = this.displayFnLot.bind(this);
  }

  private initFormRows(
    fromSelectWarehouseGroupTriggerEvent :boolean = false,
    fromPlannedQuantityChange :boolean = false,
  ) :void {
    this.formRows = this.form.get('rows') as UntypedFormArray;
    this.formRows.clear();

    let formattedItems :any[] = [];

    formattedItems = this.bomItems.map((item, index) => {
      return this.getFormattedItem(item, index, fromPlannedQuantityChange);
    });

    formattedItems?.forEach((item, index) => {
      const formRow = this.createRow(item);
      this.formRows.push(formRow);
      this.loadRowPlannedBaseQtyValidator(formRow);
      this.loadRowLotIdValidator(formRow);
      this.loadLineRfqLocations(
        item.rfqId ?? 0,
        this.warehouseIdControl?.value || item.warehouseId || 0,
        index,
        this.isUpdateFirstLoad,
        fromSelectWarehouseGroupTriggerEvent,
      );
      this.loadLotsByRfqId(
        item.rfqId ?? 0,
        item.locationId ?? 0,
        index,
        this.isUpdateFirstLoad,
        item?.lot?.lotNumber ?? '',
      );
    });

    if (this.isUpdateFirstLoad) {
      this.isUpdateFirstLoad = false;
    }
  }

  private getFormattedItem(
    item :any,
    index :number,
    fromPlannedQuantityChange :boolean = false,
  ) :any {
    let orderLine :any = null;

    if (this.isUpdate) {
      const currentOrderLine = this.currentAssemblyOrder?.orderLines[index];
      const rfqIdMatches = currentOrderLine?.rfqId === item?.rfq?.id;

      if (rfqIdMatches) {
        orderLine = currentOrderLine;
      }
    }

    let actualQty = orderLine?.actualQuantity ?? item?.plannedQuantity;
    let plannedQty = orderLine?.plannedQuantity ?? item?.plannedQuantity;
    if (fromPlannedQuantityChange && item) {
      actualQty = item?.plannedQuantity;
      plannedQty = item?.plannedQuantity;
    }

    return {
      id: orderLine?.id ?? item?.id,
      actualQty: actualQty,
      plannedQty: plannedQty,
      locationId: orderLine?.locationId ?? item?.locationId,
      warehouseId: orderLine?.location?.parentId ?? item?.warehouseId,
      rfqId: orderLine?.rfq?.id ?? item?.rfq?.id,
      lotId: orderLine?.lotId ?? item?.lotId,
      lot: orderLine?.lot,
      plannedUomId: orderLine?.plannedUomId ?? item?.uomId,
      actualUomId: orderLine?.actualUomId ?? item?.uomId,
      plannedUomName: orderLine?.actualUomName ?? item?.uom?.name,
      productName: orderLine?.rfq?.productName ?? item?.rfq?.product_name,
      internalSku: orderLine?.rfq?.internalSku ?? item?.rfq?.internal_sku,
      lotTracking: orderLine?.rfq?.lotTracking ?? item?.rfq?.lot_tracking,
      availableQty: item?.availableQuantity ?? 0,
      bomBaseQuantity: item?.bomBaseQuantity ?? 0,
      conversionFactor: item?.conversionFactor ?? 1,
    };
  }

  private createRow(item :any) :UntypedFormGroup {
    return this.fb.group({
      id: [item.id],
      actualQty: [item.actualQty],
      plannedQty: [item.plannedQty, [Validators.required]],
      locationId: [item.locationId, [Validators.required]],
      warehouseId: [item.warehouseId, [Validators.required]],
      rfqId: [item.rfqId],
      lotId: [item.lotId, [this.isFieldRequiredValidator(item.lotTracking)]],
      plannedUomId: [item.plannedUomId, [Validators.required]],
      actualUomId: [item.actualUomId, [Validators.required]],
      plannedUomName: [item.plannedUomName],
      productName: [item.productName],
      internalSku: [item.internalSku],
      lotTracking: [item.lotTracking],
      availableQty: [item.availableQty],
      bomBaseQuantity: [item.bomBaseQuantity],
      conversionFactor: [item.conversionFactor],
    });
  }

  private initPlannedQuantitySubject() :void {
    this.plannedQuantitySubject.pipe(debounceTime(500)).subscribe((event) => {
      this.onQuantityChange(event);
    });
  }

  private initActualQuantitySubject() :void {
    this.actualQuantitySubject.pipe(debounceTime(500)).subscribe((event) => {
      this.onActualQuantityChange(event);
    });
  }

  private loadRowPlannedBaseQtyValidator(row :UntypedFormGroup) :void {
    const plannedBaseControl = row.get('plannedBaseQty');
    if (plannedBaseControl) {
      plannedBaseControl.updateValueAndValidity();
      plannedBaseControl.markAsTouched();
    }
  }

  private loadRowLotIdValidator(row :UntypedFormGroup) :void {
    const lotIdControl = row.get('lotId');
    if (lotIdControl) {
      lotIdControl.updateValueAndValidity();
      lotIdControl.markAsTouched();
    }
  }

  private setupLotIdControl() :void {
    const lotIdControl = this.lotIdControl;
    lotIdControl.valueChanges
      .pipe(
        takeUntil(this.onDestroy$),
        debounceTime(1000), // Debounce for 1 second
        filter((value) => typeof value === 'string'),
      )
      .subscribe((value) => {
        this.loadingLots = true;
        this.cd.detectChanges();
        this.loadLotsBySelectedLocation(true, value);
      });
  }

  private updateCustomFieldFormValuesToJson(customFieldValuesArrayValue: ICustomFieldValue[]): void {
    this.customFieldFormValuesJson = JSON.stringify(
      customFieldValuesArrayValue.map((customFieldValue) => ({
        custom_field: customFieldValue.custom_field_id,
        value: customFieldValue.value_string ?? customFieldValue.value_number ?? customFieldValue.value_date ?? ''
      }))
    );
  }

  get isWarehouseGroupIdMatchOnEdit() :boolean {
    return (
      this.warehouseGroupIdControl?.value ===
      this.currentAssemblyOrder?.warehouseGroupId
    );
  }

  get isWarehouseIdMatchOnEdit() :boolean {
    return (
      this.warehouseIdControl?.value === this.currentAssemblyOrder?.warehouseId
    );
  }

  get formattedCustomFieldValues() :any[] {
    const formValue = this.form.value;
    return formValue.customFieldValues.map((field: any) => {
      // Determine the correct value based on the field type
      let value = '';
      if (field.value_string !== null && field.value_string !== undefined) {
        value = field.value_string;
      } else if (field.value_number !== null && field.value_number !== undefined) {
        value = field.value_number;
      } else if (field.value_date !== null && field.value_date !== undefined) {
        value = field.value_date;
      }

      return {
        custom_field: field.custom_field_id,
        value: value
      };
    });
  }
}
